import { runInAction } from "mobx";
import { IStudentSessionReportViewModel } from "../Interfaces/IStudentSessionReportViewModel";
import { APIClient } from "Application";

import { StudentSessionContextByIdResponse } from "../../../../Student/StudentReports/Endpoints/Shared/StudentSessionContextResponse";
import { UpsertStudentSessionEndpoint } from "../../Endpoints/Commands/UpsertStudentSession/Endpoint";
import { SaveSessionDocumentsEndpoint } from "../../Endpoints/Commands/SaveSessionDocumentsEndpoint";
import { SaveActivityOptionsEndpoint } from "../../Endpoints/Commands/SaveActivityOptionsEndpoint";
import { AppUrls } from "AppUrls";
import toast from "react-hot-toast";
import axios, * as Axios from "axios";
import { GetNewStudentSessionReportContextEndpoint } from "Views/Mentors/Mentor/WeeklyRota/Endpoints/GetNewStudentSessionReportContext/Endpoint";
import { GetNewStudentSessionReportContextResponse } from "Views/Mentors/Mentor/WeeklyRota/Endpoints/GetNewStudentSessionReportContext/Response";
import { GetStudentSessionContextByIdEndpoint } from "../../Endpoints/Querries/GetStudentSessionContextByIdEndpoint";

/**
 * Manages all API operations for SessionReportViewModel.
 * Responsibilities: data loading, upsert, export, file uploads, and response mapping.
 */
export class APIManager {
    public apiClient = new APIClient();

    constructor(private viewModel: IStudentSessionReportViewModel) {}

    /**
     * Load previous session data for insert mode (create new session).
     * @param studentId Required student ID
     * @param sessionId Optional session ID for rota prefill (WeeklyRota add from slot). If not provided, uses viewModel.model.id when present.
     */
    public async loadNewStudentSessionReportContext(studentId: string, sessionId?: string | null): Promise<void> {
        const effectiveSessionId = sessionId ?? (this.viewModel.model.id || null);
        await this.apiClient.sendAsync(new GetNewStudentSessionReportContextEndpoint(studentId, effectiveSessionId));
        if (this.apiClient.IsRequestSuccessful) {
            runInAction(() => {
                const response: GetNewStudentSessionReportContextResponse = this.apiClient.Response();
                this.viewModel.managers.domain.mapNewStudentSessionContextResponseToModel(response);
            });
        }
    }

    /**
     * Load existing session report by ID (edit mode).
     */
    public async loadSessionReportById(reportId: string): Promise<void> {
        await this.apiClient.sendAsync(new GetStudentSessionContextByIdEndpoint(reportId));
        if (this.apiClient.IsRequestSuccessful) {
            runInAction(() => {
                const response: StudentSessionContextByIdResponse = this.apiClient.Response();
                this.viewModel.managers.domain.mapSessionContextResponseToModel(response);
            });
        }
    }

    /**
     * Upsert (create or update) a student session report.
     */
    public async upsertStudentSession(): Promise<boolean> {
        if (!this.viewModel.canSubmitForm()) {
            this.viewModel.managers.mobx.setFormErrorMessage("There are errors in the form. Please correct them before continuing.");
            return false;
        }

        this.viewModel.managers.mobx.setIsUpserting(true);
        this.viewModel.managers.mobx.setUpsertButtonDisplayName(this.viewModel.managers.viewMode.isEditMode ? "Updating" : "Creating");

        const request = this.viewModel.managers.domain.buildUpsertStudentSessionRequest(this.viewModel.model);
        await this.apiClient.sendAsync(new UpsertStudentSessionEndpoint(), request);

        if (this.apiClient.IsRequestSuccessful) {
            runInAction(() => {
                const response: StudentSessionContextByIdResponse = this.apiClient.Response();
                this.viewModel.managers.domain.mapUpsertResponseToModel(response);
                toast.success("Student updated successfully");
                this.viewModel.managers.viewMode.switchToEditMode();
            });
            this.viewModel.managers.mobx.setIsUpserting(false);
            this.viewModel.managers.mobx.setUpsertButtonDisplayName(this.viewModel.managers.viewMode.isEditMode ? "Update" : "Create");
            return true;
        } else {
            runInAction(() => {
                toast.error(this.apiClient.ValidationMessage);
            });
            this.viewModel.managers.mobx.setIsUpserting(false);
            this.viewModel.managers.mobx.setUpsertButtonDisplayName(this.viewModel.managers.viewMode.isEditMode ? "Update" : "Create");
            return false;
        }
    }

    /**
     * Export session report as PDF.
     */
    public async exportReport(): Promise<void> {
        if (this.apiClient.IsBusy) return;

        this.viewModel.managers.mobx.setIsExporting(true);
        this.viewModel.managers.mobx.setExportButtonDisplayName("Exporting");

        try {
            const request = { id: this.viewModel.model.id };
            const config: Axios.AxiosRequestConfig = {
                responseType: "json",
                headers: {
                    Authorization: `Bearer ${sessionStorage.getItem(".auth")}`,
                    "Content-Type": "application/json",
                },
                validateStatus: (status) => true,
            };

            const response = await axios.post(AppUrls.Server.Student.StudentSessions.ExportStudentSessionReportPDF, request, config);

            if (response.status >= 200 && response.status < 300) {
                const responseData = response.data as { url: string };
                if (responseData?.url) {
                    window.open(responseData.url, "_blank", "noopener");
                } else {
                    toast.error("Invalid response: No URL provided by server", { position: "top-right", style: { marginTop: "45px" } });
                }
            } else {
                const errorData = response.data as { errors?: Array<{ message: string }> };
                const errorMessage = errorData?.errors?.[0]?.message || `Request failed with status ${response.status}`;
                toast.error(errorMessage, { position: "top-right", style: { marginTop: "45px" } });
            }
        } catch (e: any) {
            toast.error(`Export failed: ${e.message}`, { position: "top-right", style: { marginTop: "45px" } });
        } finally {
            this.viewModel.managers.mobx.setIsExporting(false);
            this.viewModel.managers.mobx.setExportButtonDisplayName("Export Report");
        }
    }

    /**
     * Upload session documents.
     */
    public uploadSessionDocuments = async () => {
        const canSubmitDoc = this.viewModel.managers.mobx.canSubmitSessionDoc;
        const hasFiles = this.viewModel.filesViewModel.model.files.length > 0;

        if (!canSubmitDoc && !hasFiles) {
            return;
        }

        await this.apiClient.sendAsync(new SaveSessionDocumentsEndpoint(this.viewModel), this.viewModel.model);

        if (this.apiClient.IsRequestSuccessful) {
            this.viewModel.managers.mobx.setShowSessionDocPhoto(false);
            this.viewModel.resetFilesViewModel();
        }
    };

    /**
     * Save activity options.
     */
    public async saveActivityOptions(): Promise<void> {
        const request = { name: this.viewModel.model.activityOptionName };
        await this.apiClient.sendAsync(new SaveActivityOptionsEndpoint(), request);
        if (this.apiClient.IsRequestSuccessful) {
            runInAction(() => {
                this.viewModel.managers.domain.mapSaveActivityOptionsResponseToModel(this.apiClient.Response());
            });
        }
    }

    /**
     * Download a document by ID.
     */
    public async downloadDocument(id: string): Promise<void> {
        try {
            const config: Axios.AxiosRequestConfig = {
                headers: { Authorization: `Bearer ${sessionStorage.getItem(".auth")}` },
            };
            const response = await axios.get(AppUrls.Server.Student.StudentSessions.GetStudentDocumentForDownloadById.replace(":id", id), config);

            if (response.data?.url) {
                window.open(response.data.url, "_blank", "noopener");
            } else {
                console.error("No SAS URL received from server");
            }
        } catch (error: any) {
            console.log("Error getting document URL:", error);
            if (error.response && (error.response.status === 401 || error.response.status === 403)) {
                alert("Link expired. Please try again to get a new link.");
            }
        }
    }

    /**
     * Reset API client error state.
     */
    public reset(): void {
        this.apiClient.reset();
    }
}
